import { useState, useCallback } from "react";
import { sendChatMessage } from "./chatbotApi";

const WELCOME_MESSAGE = {
  id: "welcome",
  role: "assistant",
  content: "Namaste! I am Aria, your personal Himalayan Travel Specialist at The Great Himalayas.\n\nI am directly connected to our live inventory to help you with:\n\n- **Live Tour Packages** — Next 30 days confirmed departures across Spiti, Himachal, Sikkim & Kashmir\n- **Mountain Cabs** — Verified private SUVs & shared seats with transparent fares\n- **Boutique Stays** — Handpicked valley retreats and heritage mountain cottages\n- **Sightseeing & Monasteries** — Curated itineraries and high-altitude road condition advice\n\nWhat kind of Himalayan adventure are you planning?",
  timestamp: new Date(),
};

/**
 * Custom hook — manages chatbot state, session persistence, generative cards, and split preview.
 */
export function useChatbot() {
  const [messages, setMessages] = useState([WELCOME_MESSAGE]);
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(() => sessionStorage.getItem("tgh_chat_session") || null);
  const [preview, setPreview] = useState(null); // { type, id, title, url }

  const sendMessage = useCallback(async (text) => {
    if (!text.trim() || isLoading) return;

    const userMsg = { id: Date.now(), role: "user", content: text.trim(), timestamp: new Date() };
    setMessages((prev) => [...prev, userMsg]);
    setIsLoading(true);

    try {
      const data = await sendChatMessage({ message: text.trim(), sessionId });

      const assistantMsg = {
        id: Date.now() + 1,
        role: "assistant",
        content: data.response,
        domain: data.domain,
        toolsUsed: data.toolsUsed,
        preview: data.preview || null,
        cards: data.cards || null,
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, assistantMsg]);

      // Automatically open split-screen preview if a live package or route is discussed
      if (data.preview?.url) {
        setPreview(data.preview);
      }

      // Persist session ID
      if (data.sessionId && data.sessionId !== sessionId) {
        setSessionId(data.sessionId);
        sessionStorage.setItem("tgh_chat_session", data.sessionId);
      }
    } catch (err) {
      console.error("[useChatbot] Error:", err);
      const errMsg = {
        id: Date.now() + 1,
        role: "assistant",
        content: "I encountered a brief connection issue. Please try again or reach our team directly at +91 9883587965.",
        isError: true,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errMsg]);
    } finally {
      setIsLoading(false);
    }
  }, [sessionId, isLoading]);

  const clearChat = useCallback(() => {
    setMessages([WELCOME_MESSAGE]);
    setSessionId(null);
    setPreview(null);
    sessionStorage.removeItem("tgh_chat_session");
  }, []);

  const closePreview = useCallback(() => {
    setPreview(null);
  }, []);

  return {
    messages,
    isLoading,
    preview,
    setPreview,
    closePreview,
    sendMessage,
    clearChat,
  };
}
